import React, { useState, useEffect, useMemo } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { quotesAPI } from '../../services/quotesAPI';
import { leadsAPI, settingsAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import { FileText, Plus, Trash2, Search, CheckCircle, TrendingUp } from 'lucide-react';
import QuoteStats        from './Quotes/QuoteStats';
import QuoteCard         from './Quotes/QuoteCard';
import QuotePreviewModal from './Quotes/QuotePreviewModal';
import '../Admin/AdminDashboard.css';
import './AdminQuotes.css';

const AdminQuotes = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [useLeadData, setUseLeadData] = useState(true); // NEW: Toggle between lead/manual entry
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [companyDefaults, setCompanyDefaults] = useState({
    name: 'MeccaFlex S.r.l.',
    address: 'Via Example 123',
    city: 'Roma',
    postalCode: '00100',
    country: 'Italia',
    vatNumber: '',
    iban: '',
    email: 'info@meccaflex.com',
    phone: '',
    website: 'www.meccaflex.com'
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const debouncedSearch = useDebounce(filters.search, 300);

  // Form state for new/edit quote
  const [formData, setFormData] = useState({
    leadId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCompany: '',
    paymentTerms: 'net30',
    paymentTermsCustom: '',
    deliveryTimeline: 14,
    projectReference: '',
    specialInstructions: '',
    accountManager: '',
    accountManagerEmail: '',
    language: 'it',
    currency: 'EUR',
    companyInfo: {
      name: 'MeccaFlex S.r.l.',
      address: '',
      city: '',
      postalCode: '',
      country: 'Italia',
      vatNumber: '',
      iban: '',
      email: '',
      phone: '',
      website: ''
    },
    items: [
      { description: '', material: '', quantity: 1, weight: 0, pricePerUnit: 0 }
    ],
    urgentSurcharge: 0,
    shippingCost: 0,
    taxRate: 22,
    notes: '',
    validDays: 30
  });

  useEffect(() => {
    loadQuotes();
  }, [debouncedSearch, filters.status]);

  useEffect(() => {
    loadStats();
    loadLeads();
  }, []);

  useEffect(() => {
    loadBusinessSettings();
  }, []);

  const loadBusinessSettings = async () => {
    try {
      const response = await settingsAPI.getAll({ category: 'business_info' });
      const settingsArr = response.data.settings;
      if (settingsArr && settingsArr.length > 0) {
        const s = settingsArr[0].settings;
        setCompanyDefaults(prev => ({
          ...prev,
          name: s.companyName || prev.name,
          vatNumber: s.vatNumber || prev.vatNumber,
          iban: s.iban || prev.iban,
          email: s.email || prev.email,
          phone: s.phone || prev.phone,
          address: s.address || prev.address,
          city: s.city || prev.city,
          postalCode: s.postalCode || prev.postalCode,
          country: s.country || prev.country,
          website: s.website || prev.website
        }));
      }
    } catch (error) {
      console.error('Could not load business settings for quote defaults:', error);
    }
  };

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const response = await quotesAPI.getAll({ ...filters, search: debouncedSearch });
      setQuotes(response.data.quotes || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await quotesAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadLeads = async () => {
    try {
      // Load ALL leads, not just new/contacted
      const response = await leadsAPI.getAll({ status: '' });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const handleCreateQuote = () => {
    setEditingQuote(null);
    setUseLeadData(true);
    setAiSuggestion(null);
    setFormData({
      leadId: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerCompany: '',
      paymentTerms: 'net30',
      paymentTermsCustom: '',
      deliveryTimeline: 14,
      projectReference: '',
      specialInstructions: '',
      accountManager: '',
      accountManagerEmail: '',
      language: 'it',
      currency: 'EUR',
      companyInfo: {
        ...companyDefaults
      },
      urgentSurcharge: 0,
      shippingCost: 0,
      taxRate: 22,
      notes: '',
      validDays: 30
    });
    setShowModal(true);
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setUseLeadData(!!quote.lead);
    setAiSuggestion(null);
    setFormData({
      leadId: quote.lead?._id || '',
      customerName: quote.customerName,
      customerEmail: quote.customerEmail,
      customerPhone: quote.customerPhone || '',
      customerCompany: quote.customerCompany || '',
      paymentTerms: quote.paymentTerms || 'net30',
      paymentTermsCustom: quote.paymentTermsCustom || '',
      deliveryTimeline: quote.deliveryTimeline || 14,
      projectReference: quote.projectReference || '',
      specialInstructions: quote.specialInstructions || '',
      accountManager: quote.accountManager || '',
      accountManagerEmail: quote.accountManagerEmail || '',
      language: quote.language || 'it',
      currency: quote.currency || 'EUR',
      companyInfo: quote.companyInfo || { ...companyDefaults },
      items: quote.items,
      urgentSurcharge: quote.urgentSurcharge,
      shippingCost: quote.shippingCost,
      taxRate: quote.taxRate,
      notes: quote.notes || '',
      validDays: Math.max(1, Math.ceil((new Date(quote.validUntil) - new Date()) / (1000 * 60 * 60 * 24)))
    });
    setShowModal(true);
  };

  // NEW: Handle lead selection - auto-fill customer data
  const handleLeadChange = (leadId) => {
    const selectedLead = leads.find(l => l._id === leadId);
    if (selectedLead) {
      setFormData({
        ...formData,
        leadId: leadId,
        customerName: selectedLead.name,
        customerEmail: selectedLead.email,
        customerPhone: selectedLead.phone || '',
        customerCompany: selectedLead.company || ''
      });
    } else {
      setFormData({
        ...formData,
        leadId: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customerName.trim()) {
      toast.error(t('admin.quotes.messages.customerRequired'));
      return;
    }
    if (!formData.customerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
      toast.error(t('admin.quotes.messages.emailInvalid'));
      return;
    }
    if (!(formData.items || []).some(item => item.description.trim() && item.quantity > 0 && item.pricePerUnit > 0)) {
      toast.error(t('admin.quotes.messages.itemRequired'));
      return;
    }

    try {
      if (editingQuote) {
        await quotesAPI.update(editingQuote._id, formData);
      } else {
        await quotesAPI.create(formData);
      }
      setShowModal(false);
      loadQuotes();
      loadStats();
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error(t('admin.quotes.messages.saveError'));
    }
  };

  const handleSendQuote = async (quoteId) => {
    if (!window.confirm(t('admin.quotes.messages.sendConfirm'))) return;
    
    try {
      await quotesAPI.send(quoteId);
      toast.success(t('admin.quotes.messages.sendSuccess'));
      loadQuotes();
      loadStats();
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(t('admin.quotes.messages.sendError'));
    }
  };

  const handleDownloadPDF = async (quoteId, quoteNumber) => {
    try {
      const response = await quotesAPI.downloadPDF(quoteId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-${quoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('admin.quotes.messages.downloadError'));
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm(t('admin.quotes.messages.deleteConfirm'))) return;
    
    try {
      await quotesAPI.delete(quoteId);
      loadQuotes();
      loadStats();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error(error.response?.data?.message || t('admin.quotes.messages.deleteError'));
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...(formData.items || []), { description: '', material: '', quantity: 1, weight: 0, pricePerUnit: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: (formData.items || []).filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(formData.items || [])];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const subtotal = useMemo(() => {
    return (formData.items || []).reduce((sum, item) => {
      return sum + (item.quantity * item.pricePerUnit);
    }, 0);
  }, [formData.items]);

  const quoteTotal = useMemo(() => {
    const beforeTax = subtotal + (formData.urgentSurcharge || 0) + (formData.shippingCost || 0);
    const taxAmount = (beforeTax * (formData.taxRate || 22)) / 100;
    return beforeTax + taxAmount;
  }, [subtotal, formData.urgentSurcharge, formData.shippingCost, formData.taxRate]);

  const handleGetAISuggestion = async () => {
    // Validate that we have at least one item with description
    const hasValidItems = (formData.items || []).some(item => item.description && item.description.trim() !== '');

    if (!hasValidItems) {
      toast.info(t('admin.quotes.messages.aiNeedItems'));
      return;
    }

    setLoadingAI(true);
    try {
      const response = await quotesAPI.getAISuggestion({
        items: (formData.items || []).map(item => ({
          description: item.description,
          material: item.material,
          quantity: item.quantity,
          weight: item.weight
        })),
        customer: {
          name: formData.customerName,
          company: formData.customerCompany
        },
        priority: formData.urgentSurcharge > 0 ? 'urgent' : 'normal'
      });

      if (response.data.success) {
        setAiSuggestion(response.data.suggestion);
      } else {
        toast.warning(t('admin.quotes.messages.aiBasicSuggestions'));
        setAiSuggestion(response.data.suggestion);
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      toast.error(t('admin.quotes.messages.aiError'));
    } finally {
      setLoadingAI(false);
    }
  };

  const handleApplyAISuggestion = () => {
    if (!aiSuggestion) return;

    // Apply AI suggestions to form
    const updatedItems = (formData.items || []).map((item, index) => {
      const aiItem = aiSuggestion.items?.[index];
      if (aiItem) {
        return {
          ...item,
          pricePerUnit: aiItem.suggestedUnitPrice || item.pricePerUnit,
          weight: aiItem.materialGrams || item.weight
        };
      }
      return item;
    });

    setFormData({
      ...formData,
      items: updatedItems,
      shippingCost: aiSuggestion.suggestedShipping || formData.shippingCost
    });

    toast.success(t('admin.quotes.messages.aiApplied'));
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <header className="content-header">
          <div>
            <h1>{t('admin.quotes.title')}</h1>
            <p>{t('admin.quotes.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <LanguageToggle />
            <button onClick={handleCreateQuote} className="admin-btn admin-btn-primary">
              <Plus size={18} />
              {t('admin.quotes.new')}
            </button>
          </div>
        </header>

        {/* Statistics */}
        <QuoteStats stats={stats} />

        {/* Filters */}
        <div className="filters-bar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder={t('admin.quotes.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          <select 
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">{t('admin.leads.allStatuses')}</option>
            <option value="draft">{t('admin.quotes.status.draft')}</option>
            <option value="sent">{t('admin.quotes.status.sent')}</option>
            <option value="accepted">{t('admin.quotes.status.accepted')}</option>
            <option value="rejected">{t('admin.quotes.status.rejected')}</option>
            <option value="expired">{t('admin.quotes.status.expired')}</option>
          </select>
        </div>

        {/* Quotes List */}
        <div className="quotes-list">
          {loading ? (
            <div className="loading-spinner">{t('admin.common.loading')}</div>
          ) : quotes.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>{t('admin.quotes.empty')}</p>
            </div>
          ) : (
            quotes.map(quote => (
              <QuoteCard
                key={quote._id}
                quote={quote}
                onEdit={handleEditQuote}
                onSend={handleSendQuote}
                onDownload={handleDownloadPDF}
                onDelete={handleDeleteQuote}
                onView={(q) => setViewingQuote(q)}
              />
            ))
          )}
        </div>

        {/* Readonly Quote Preview Modal */}
        {viewingQuote && (
          <QuotePreviewModal
            quote={viewingQuote}
            onClose={() => setViewingQuote(null)}
            onSend={(id) => { handleSendQuote(id); setViewingQuote(null); }}
            onDownload={handleDownloadPDF}
          />
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content quote-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingQuote ? t('admin.quotes.editTitle') : t('admin.quotes.newTitle')}</h2>
                <button onClick={() => setShowModal(false)} className="modal-close" aria-label={t('admin.common.close')}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="quote-form">
                
                {/* Customer Information Section */}
                <div className="form-section">
                  <h3>{t('admin.quotes.customerInfo')}</h3>
                  
                  {!editingQuote && (
                    <div className="toggle-group">
                      <button
                        type="button"
                        className={`toggle-btn ${useLeadData ? 'active' : ''}`}
                        onClick={() => {
                          setUseLeadData(true);
                          setFormData({ ...formData, leadId: '', customerName: '', customerEmail: '', customerPhone: '', customerCompany: '' });
                        }}
                      >
                        {t('admin.quotes.useLeadData')}
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${!useLeadData ? 'active' : ''}`}
                        onClick={() => {
                          setUseLeadData(false);
                          setFormData({ ...formData, leadId: '' });
                        }}
                      >
                        {t('admin.quotes.manualEntry')}
                      </button>
                    </div>
                  )}

                  {useLeadData && !editingQuote ? (
                    <div className="form-group">
                      <label>{t('admin.quotes.selectLead')}</label>
                      <select 
                        value={formData.leadId}
                        onChange={(e) => handleLeadChange(e.target.value)}
                      >
                        <option value="">{t('admin.quotes.manualEntry')}</option>
                        {leads.map(lead => (
                          <option key={lead._id} value={lead._id}>
                            {lead.company || lead.name} | {lead.name} | {lead.email} | {lead.phone || 'N/A'}
                          </option>
                        ))}
                      </select>
                      <small style={{ color: '#71717a', marginTop: '0.25rem', display: 'block' }}>
                        {t('admin.quotes.customerInfoHint')}
                      </small>
                    </div>
                  ) : null}

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.customerName')} *</label>
                      <input
                        type="text"
                        value={formData.customerName}
                        onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                        required
                        disabled={editingQuote}
                        placeholder="Mario Rossi"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.customerEmail')} *</label>
                      <input
                        type="email"
                        value={formData.customerEmail}
                        onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                        required
                        disabled={editingQuote}
                        placeholder="mario.rossi@email.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.customerPhone')}</label>
                      <input
                        type="tel"
                        value={formData.customerPhone}
                        onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                        disabled={editingQuote}
                        placeholder="+39 123 456 7890"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.customerCompany')}</label>
                      <input
                        type="text"
                        value={formData.customerCompany}
                        onChange={(e) => setFormData({ ...formData, customerCompany: e.target.value })}
                        disabled={editingQuote}
                        placeholder="Nome Azienda S.r.l."
                      />
                    </div>
                  </div>
                </div>

                {/* Business Terms Section */}
                <div className="form-section">
                  <h3>{t('admin.quotes.businessTerms')}</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.paymentTerms')} *</label>
                      <select
                        value={formData.paymentTerms}
                        onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                        required
                      >
                        <option value="immediate">{t('admin.quotes.paymentImmediate')}</option>
                        <option value="net15">{t('admin.quotes.paymentNet30')}</option>
                        <option value="net30">{t('admin.quotes.paymentNet30')}</option>
                        <option value="net60">{t('admin.quotes.paymentNet60')}</option>
                        <option value="deposit30">30% Deposit</option>
                        <option value="deposit50">50% Deposit</option>
                        <option value="custom">{t('admin.quotes.paymentCustom')}</option>
                      </select>
                    </div>

                    {formData.paymentTerms === 'custom' && (
                      <div className="form-group">
                        <label>{t('admin.quotes.customTerms')} *</label>
                        <input
                          type="text"
                          value={formData.paymentTermsCustom}
                          onChange={(e) => setFormData({ ...formData, paymentTermsCustom: e.target.value })}
                          required
                          placeholder={t('admin.quotes.customTermsPlaceholder')}
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>{t('admin.quotes.deliveryTimeline')} *</label>
                      <input
                        type="number"
                        value={formData.deliveryTimeline}
                        onChange={(e) => setFormData({ ...formData, deliveryTimeline: parseInt(e.target.value) || 14 })}
                        min="1"
                        required
                        placeholder="14"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.projectReference')}</label>
                      <input
                        type="text"
                        value={formData.projectReference}
                        onChange={(e) => setFormData({ ...formData, projectReference: e.target.value })}
                        placeholder={t('admin.quotes.projectReferencePlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.validDays')} *</label>
                      <input
                        type="number"
                        value={formData.validDays}
                        onChange={(e) => setFormData({ ...formData, validDays: parseInt(e.target.value) || 30 })}
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('admin.quotes.specialInstructions')}</label>
                    <textarea
                      value={formData.specialInstructions}
                      onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                      rows={3}
                      placeholder={t('admin.quotes.specialInstructionsPlaceholder')}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Account Manager</label>
                      <input
                        type="text"
                        value={formData.accountManager}
                        onChange={(e) => setFormData({ ...formData, accountManager: e.target.value })}
                        placeholder="Sales Manager Name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Account Manager Email</label>
                      <input
                        type="email"
                        value={formData.accountManagerEmail}
                        onChange={(e) => setFormData({ ...formData, accountManagerEmail: e.target.value })}
                        placeholder="manager@meccaflex.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Information Section */}
                <div className="form-section">
                  <h3>{t('admin.quotes.companyInfo')}</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.companyName')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.name}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, name: e.target.value } })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.vatNumber')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.vatNumber}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, vatNumber: e.target.value } })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.address')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.address}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, address: e.target.value } })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.postalCode')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.postalCode}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, postalCode: e.target.value } })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.city')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.city}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, city: e.target.value } })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.country')} *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.country}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, country: e.target.value } })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.leads.modal.email')} *</label>
                      <input
                        type="email"
                        value={formData.companyInfo.email}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, email: e.target.value } })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.phone')} *</label>
                      <input
                        type="tel"
                        value={formData.companyInfo.phone}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, phone: e.target.value } })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>IBAN *</label>
                      <input
                        type="text"
                        value={formData.companyInfo.iban}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, iban: e.target.value } })}
                        required
                        placeholder="IT60 X054 2811 1010 0000 0123 456"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.website')}</label>
                      <input
                        type="text"
                        value={formData.companyInfo.website}
                        onChange={(e) => setFormData({ ...formData, companyInfo: { ...formData.companyInfo, website: e.target.value } })}
                        placeholder="www.meccaflex.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Locale Settings Section */}
                <div className="form-section">
                  <h3>{t('admin.quotes.quoteSettings')}</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('admin.quotes.pdfLanguage')} *</label>
                      <select
                        value={formData.language}
                        onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                        required
                      >
                        <option value="it">Italiano 🇮🇹</option>
                        <option value="en">English 🇬🇧</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t('admin.quotes.currency')} *</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        required
                      >
                        <option value="EUR">Euro (€)</option>
                        <option value="USD">US Dollar ($)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#10b981' }}>{t('admin.quotes.items')}</h3>
                
                {(formData.items || []).map((item, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <input
                        type="text"
                        placeholder={t('admin.quotes.itemDescription') + ' *'}
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        required
                      />
                      <input
                        type="text"
                        placeholder={t('admin.quotes.itemMaterial') + ' *'}
                        value={item.material}
                        onChange={(e) => updateItem(index, 'material', e.target.value)}
                        required
                      />
                      <input
                        type="number"
                        placeholder={t('admin.quotes.itemQuantity')}
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                        min="1"
                        required
                        style={{ width: '80px' }}
                      />
                      <input
                        type="number"
                        placeholder={t('admin.quotes.itemWeight')}
                        value={item.weight}
                        onChange={(e) => updateItem(index, 'weight', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        style={{ width: '100px' }}
                      />
                      <input
                        type="number"
                        placeholder={t('admin.quotes.itemPrice')}
                        value={item.pricePerUnit}
                        onChange={(e) => updateItem(index, 'pricePerUnit', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        required
                        style={{ width: '100px' }}
                      />
                      <span style={{ minWidth: '80px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>
                        €{(item.quantity * item.pricePerUnit).toFixed(2)}
                      </span>
                    </div>
                    {(formData.items || []).length > 1 && (
                      <button 
                        type="button"
                        onClick={() => removeItem(index)}
                        className="remove-item-btn"
                        aria-label={t('admin.orders.removeItem')}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addItem}
                  className="add-item-btn"
                >
                  <Plus size={16} />
                  {t('admin.quotes.addItem')}
                </button>

                {/* AI Pricing Suggestion Section */}
                <div className="ai-suggestion-section" style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <TrendingUp size={20} />
                      AI Pricing Suggestions
                    </h3>
                    <button
                      type="button"
                      onClick={handleGetAISuggestion}
                      disabled={loadingAI}
                      className="admin-btn admin-btn-primary"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      {loadingAI ? (
                        <>{t('admin.quotes.aiAnalyzing')}</>
                      ) : (
                        <>
                          <TrendingUp size={16} />
                          {t('admin.quotes.getAISuggestion')}
                        </>
                      )}
                    </button>
                  </div>

                  {aiSuggestion && (
                    <div className="ai-results" style={{ marginTop: '1rem' }}>
                      <div style={{ background: 'white', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                          <div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>{t('admin.quotes.ai.subtotal')}</p>
                            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981', margin: '0.25rem 0 0 0' }}>
                              €{aiSuggestion.suggestedSubtotal?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>{t('admin.quotes.ai.profitMargin')}</p>
                            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', margin: '0.25rem 0 0 0' }}>
                              {aiSuggestion.profitMargin || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>{t('admin.quotes.ai.deliveryTime')}</p>
                            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f59e0b', margin: '0.25rem 0 0 0' }}>
                              {aiSuggestion.recommendedDeliveryTime || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>{t('admin.quotes.ai.competitive')}</p>
                            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#8b5cf6', margin: '0.25rem 0 0 0' }}>
                              {aiSuggestion.competitiveAnalysis || 'N/A'}
                            </p>
                          </div>
                        </div>

                        {aiSuggestion.recommendations && aiSuggestion.recommendations.length > 0 && (
                          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '4px', borderLeft: '3px solid #10b981' }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#059669', margin: '0 0 0.5rem 0' }}>{t('admin.quotes.ai.recommendations')}</p>
                            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#065f46' }}>
                              {aiSuggestion.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {aiSuggestion.items && aiSuggestion.items.length > 0 && (
                          <div style={{ marginTop: '1rem' }}>
                            <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' }}>{t('admin.quotes.ai.suggestedPrices')}</p>
                            {aiSuggestion.items.map((item, index) => (
                              <div key={index} style={{ fontSize: '0.875rem', color: '#64748b', padding: '0.5rem', background: '#f8fafc', borderRadius: '4px', marginBottom: '0.5rem' }}>
                                <strong>{t('admin.quotes.ai.item', { n: index + 1 })}:</strong> €{item.suggestedUnitPrice?.toFixed(2) || '0.00'}
                                {item.reasoning && (
                                  <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#94a3b8' }}>
                                    ({item.reasoning})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={handleApplyAISuggestion}
                        className="admin-btn admin-btn-primary"
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                      >
                        <CheckCircle size={16} />
                        {t('admin.quotes.ai.apply')}
                      </button>
                    </div>
                  )}

                  {!aiSuggestion && !loadingAI && (
                    <p style={{ fontSize: '0.875rem', color: '#64748b', textAlign: 'center', margin: '1rem 0 0 0' }}>
                      {t('admin.quotes.ai.hint')}
                    </p>
                  )}
                </div>

                <div className="form-row" style={{ marginTop: '2rem' }}>
                  <div className="form-group">
                    <label>{t('admin.quotes.urgentSurcharge')}</label>
                    <input
                      type="number"
                      value={formData.urgentSurcharge}
                      onChange={(e) => setFormData({ ...formData, urgentSurcharge: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.quotes.shippingCost')}</label>
                    <input
                      type="number"
                      value={formData.shippingCost}
                      onChange={(e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.quotes.taxRate')}</label>
                    <input
                      type="number"
                      value={formData.taxRate}
                      onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 22 })}
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.quotes.validDays')}</label>
                    <input
                      type="number"
                      value={formData.validDays}
                      onChange={(e) => setFormData({ ...formData, validDays: parseInt(e.target.value) || 30 })}
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('admin.quotes.notes')}</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder={t('admin.quotes.notesPlaceholder')}
                  />
                </div>

                <div className="quote-summary">
                  <div className="summary-row">
                    <span>{t('admin.quotes.subtotal')}:</span>
                    <span>€{subtotal.toFixed(2)}</span>
                  </div>
                  {formData.urgentSurcharge > 0 && (
                    <div className="summary-row">
                      <span>{t('admin.quotes.urgentSurcharge').replace(' (€)', '')}:</span>
                      <span>€{formData.urgentSurcharge.toFixed(2)}</span>
                    </div>
                  )}
                  {formData.shippingCost > 0 && (
                    <div className="summary-row">
                      <span>{t('admin.quotes.shippingCost').replace(' (€)', '')}:</span>
                      <span>€{formData.shippingCost.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span>{t('admin.quotes.taxAmount')} ({formData.taxRate}%):</span>
                    <span>€{((subtotal + formData.urgentSurcharge + formData.shippingCost) * formData.taxRate / 100).toFixed(2)}</span>
                  </div>
                  <div className="summary-row total-row">
                    <span>{t('admin.quotes.total').toUpperCase()}:</span>
                    <span>€{quoteTotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowModal(false)} className="admin-btn">
                    {t('admin.quotes.actions.cancel')}
                  </button>
                  <button type="submit" className="admin-btn admin-btn-primary">
                    {editingQuote ? t('admin.quotes.actions.update') : t('admin.quotes.actions.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuotes;
